import { Vec2, Camera, Body } from '../types.js';

export interface PlaceState {
  mode: 'idle' | 'placing' | 'grabbing';
  screenStart: Vec2;
  screenCurrent: Vec2;
  worldStart: Vec2;
  body: Body | null;
}

export class InputManager {
  public placeState: PlaceState = { mode: 'idle', screenStart: {x:0,y:0}, screenCurrent: {x:0,y:0}, worldStart: {x:0,y:0}, body: null };
  private panActive: boolean = false;
  private panStart: Vec2 = {x:0,y:0};
  private panCamStart: Vec2 = {x:0,y:0};

  get placeMode() { return this.placeState.mode; }
  get screenStart() { return this.placeState.screenStart; }
  get screenCurrent() { return this.placeState.screenCurrent; }
  get worldStart() { return this.placeState.worldStart; }
  get grabbedBody() { return this.placeState.body; }
  get isDragging() {
    if (this.placeState.mode === 'idle') return false;
    const dx = this.placeState.screenCurrent.x - this.placeState.screenStart.x;
    const dy = this.placeState.screenCurrent.y - this.placeState.screenStart.y;
    return (dx*dx + dy*dy) > 25; // > 5px
  }
  get isGrabbing() { return this.placeState.mode === 'grabbing'; }
  get isPanActive() { return this.panActive; }

  onMouseDown(e: MouseEvent, camera: Camera, bodies: Body[]) {
    if (e.button === 0) {
      this.placeState.mode = 'placing';
      this.placeState.screenStart = { x: e.clientX, y: e.clientY };
      this.placeState.screenCurrent = { x: e.clientX, y: e.clientY };

      const worldPos = {
        x: (e.clientX - window.innerWidth / 2) / camera.zoom + camera.centerX,
        y: (e.clientY - window.innerHeight / 2) / camera.zoom + camera.centerY,
      };
      this.placeState.worldStart = { x: worldPos.x, y: worldPos.y };

      // Check if clicking on an existing body
      let closestBody: Body | null = null;
      let closestDist = Infinity;
      for (const b of bodies) {
        const screenX = (b.x - camera.centerX) * camera.zoom + window.innerWidth / 2;
        const screenY = (b.y - camera.centerY) * camera.zoom + window.innerHeight / 2;
        const dx = e.clientX - screenX;
        const dy = e.clientY - screenY;
        const hitRadius = Math.max(b.radius * camera.zoom, 15) + 10;
        if (dx*dx + dy*dy < hitRadius*hitRadius && dx*dx + dy*dy < closestDist*closestDist) {
          closestDist = dx*dx + dy*dy;
          closestBody = b;
        }
      }

      if (closestBody) {
        this.placeState.mode = 'grabbing';
        this.placeState.body = closestBody;
        this.placeState.worldStart = { x: closestBody.x, y: closestBody.y };
      }
    } else if (e.button === 2) {
      this.panActive = true;
      this.panStart = { x: e.clientX, y: e.clientY };
      this.panCamStart = { x: camera.centerX, y: camera.centerY };
      this.placeState.mode = 'idle';
    }
  }

  onMouseMove(e: MouseEvent, camera: Camera) {
    this.placeState.screenCurrent = { x: e.clientX, y: e.clientY };

    if (this.panActive) {
      const dx = e.clientX - this.panStart.x;
      const dy = e.clientY - this.panStart.y;
      camera.centerX = this.panCamStart.x - dx / camera.zoom;
      camera.centerY = this.panCamStart.y - dy / camera.zoom;
    }

    if (this.placeState.mode === 'grabbing' && this.placeState.body) {
      const worldPos = {
        x: (e.clientX - window.innerWidth / 2) / camera.zoom + camera.centerX,
        y: (e.clientY - window.innerHeight / 2) / camera.zoom + camera.centerY,
      };
      this.placeState.body.x = worldPos.x;
      this.placeState.body.y = worldPos.y;
    }
  }

  onMouseUp(e: MouseEvent) {
    if (e.button === 2) {
      this.panActive = false;
    }
  }

  onWheel(e: WheelEvent, camera: Camera) {
    e.preventDefault();
    const worldBefore = {
      x: (e.clientX - window.innerWidth / 2) / camera.zoom + camera.centerX,
      y: (e.clientY - window.innerHeight / 2) / camera.zoom + camera.centerY,
    };
    const newZoom = Math.max(0.05, Math.min(20, camera.zoom * Math.pow(1.1, -e.deltaY * 0.001)));
    camera.zoom = newZoom;
    const worldAfter = {
      x: (e.clientX - window.innerWidth / 2) / camera.zoom + camera.centerX,
      y: (e.clientY - window.innerHeight / 2) / camera.zoom + camera.centerY,
    };
    camera.centerX += worldBefore.x - worldAfter.x;
    camera.centerY += worldBefore.y - worldAfter.y;
  }

  clear() {
    this.placeState = { mode: 'idle', screenStart: {x:0,y:0}, screenCurrent: {x:0,y:0}, worldStart: {x:0,y:0}, body: null };
  }
}
